import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Clock, Shield, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import analytics from "@/utils/analytics";
import { checkAndSaveReferral } from "@/utils/referralTracking";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DBACheckLanding = () => {
  // Track landing page view and check for referral
  useEffect(() => {
    analytics.landingPageView();
    checkAndSaveReferral(API_URL);
  }, []);

  const benefits = [
    "Schat direct uw risico in",
    "Gebaseerd op actuele wetgeving",
    "Anoniem en veilig",
    "Resultaat binnen 5 minuten",
    "Optioneel: ontvang een compleet persoonlijk rapport"
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 mb-6" data-testid="dba-hero-label">
                DBA Check
              </p>
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-zinc-900 mb-6" data-testid="dba-hero-title">
                Weet waar u staat als{" "}
                <span className="font-medium">zzp'er</span>
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-zinc-600 mb-8 max-w-xl" data-testid="dba-hero-description">
                De nieuwe handhaving van de wet DBA komt eraan. Voorkom verrassingen 
                en krijg direct inzicht in uw situatie met onze gratis risicocheck.
              </p>
              
              {/* Benefits list */}
              <ul className="space-y-3 mb-10">
                {benefits.slice(0, -1).map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                    className="flex items-center gap-3 text-zinc-700"
                    data-testid={`benefit-${index}`}
                  >
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span>{benefit}</span>
                  </motion.li>
                ))}
                {/* Premium report benefit - styled differently */}
                <motion.li
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                  className="flex items-center gap-3 text-zinc-700 mt-4 pt-3 border-t border-zinc-200"
                  data-testid="benefit-premium"
                >
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs">★</span>
                  </div>
                  <span>
                    Optioneel: <span className="font-medium text-blue-600">compleet persoonlijk rapport</span> met advies op maat
                  </span>
                </motion.li>
              </ul>

              <Link to="/dba-check/vragenlijst" data-testid="start-check-link">
                <Button 
                  className="rounded-full px-10 py-6 text-base font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white btn-hover shadow-lg shadow-blue-500/20"
                  data-testid="start-check-btn"
                >
                  Start de DBA Check
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              
              <p className="mt-4 text-sm text-zinc-500 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Ongeveer 5 minuten
              </p>
            </motion.div>

            {/* Right: Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.pexels.com/photos/1438081/pexels-photo-1438081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Freelancer aan het werk"
                  className="w-full h-auto object-cover aspect-[4/3]"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is DBA Section */}
      <section className="py-16 md:py-24 px-6 md:px-16 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-zinc-900 mb-6" data-testid="what-is-dba-title">
              Wat is schijnzelfstandigheid?
            </h2>
            <p className="text-lg text-zinc-600 leading-relaxed">
              Schijnzelfstandigheid ontstaat wanneer iemand formeel als zzp'er werkt, 
              maar feitelijk een arbeidsrelatie heeft met de opdrachtgever. Dit kan 
              leiden tot naheffingen en boetes voor beide partijen.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Risk Factor 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white border border-zinc-200/60 shadow-sm rounded-2xl p-8"
              data-testid="risk-factor-1"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-5">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-outfit text-lg font-medium text-zinc-900 mb-3">
                Gezagsverhouding
              </h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Bepaalt de opdrachtgever hoe, wanneer en waar u werkt? Dit wijst 
                op een mogelijk dienstverband.
              </p>
            </motion.div>

            {/* Risk Factor 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white border border-zinc-200/60 shadow-sm rounded-2xl p-8"
              data-testid="risk-factor-2"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-5">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-outfit text-lg font-medium text-zinc-900 mb-3">
                Economische afhankelijkheid
              </h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Werkt u langdurig voor één opdrachtgever? Dit vergroot het risico 
                op schijnzelfstandigheid.
              </p>
            </motion.div>

            {/* Risk Factor 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white border border-zinc-200/60 shadow-sm rounded-2xl p-8"
              data-testid="risk-factor-3"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-5">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-outfit text-lg font-medium text-zinc-900 mb-3">
                Vervangbaarheid
              </h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Kunt u zich niet laten vervangen door een ander? Dit is een 
                belangrijk signaal voor de Belastingdienst.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Consequences/Urgency Section */}
      <section className="py-16 md:py-24 px-6 md:px-16 lg:px-24 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Waarom dit belangrijk is</span>
            </div>
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-4">
              De gevolgen kunnen groot zijn
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 md:p-10"
          >
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Veel bedrijven werken met zzp'ers op een manier die in de praktijk toch als 
              loondienst kan worden gezien.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Risico situaties */}
              <div>
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  </span>
                  Denk aan situaties zoals:
                </h3>
                <ul className="space-y-3">
                  {[
                    "Vaste werktijden",
                    "Werken voor één opdrachtgever",
                    "Commissie/performance constructies",
                    "Dagelijkse aansturing alsof iemand werknemer is"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Gevolgen */}
              <div>
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  </span>
                  Mogelijke gevolgen:
                </h3>
                <ul className="space-y-3">
                  {[
                    "Naheffingen",
                    "Correcties",
                    "Boetes",
                    "Juridische kosten"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Warning box */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-5 mb-8">
              <p className="text-amber-200 text-center">
                In sommige situaties kunnen kosten oplopen tot{" "}
                <span className="font-semibold text-amber-300">duizenden of zelfs tienduizenden euro's</span>.
              </p>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Link to="/dba-check/vragenlijst">
                <Button 
                  className="rounded-full px-8 py-6 text-base font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white shadow-lg shadow-blue-500/20"
                  data-testid="urgency-cta-btn"
                >
                  Check hoe risicovol jouw situatie is
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works Section */}
      <section className="py-16 md:py-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-zinc-900 mb-4" data-testid="how-it-works-title">
              Hoe werkt het?
            </h2>
            <p className="text-lg text-zinc-600">
              In drie stappen naar duidelijkheid
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
              data-testid="step-1"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] flex items-center justify-center mx-auto mb-6">
                <span className="font-outfit text-2xl font-medium text-white">1</span>
              </div>
              <h3 className="font-outfit text-xl font-medium text-zinc-900 mb-3">
                Beantwoord de vragen
              </h3>
              <p className="text-zinc-600 leading-relaxed">
                12 gerichte vragen over uw werksituatie en relatie met uw opdrachtgever.
              </p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
              data-testid="step-2"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] flex items-center justify-center mx-auto mb-6">
                <span className="font-outfit text-2xl font-medium text-white">2</span>
              </div>
              <h3 className="font-outfit text-xl font-medium text-zinc-900 mb-3">
                Wij berekenen uw score
              </h3>
              <p className="text-zinc-600 leading-relaxed">
                Op basis van uw antwoorden bepalen wij uw risicoprofiel.
              </p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
              data-testid="step-3"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] flex items-center justify-center mx-auto mb-6">
                <span className="font-outfit text-2xl font-medium text-white">3</span>
              </div>
              <h3 className="font-outfit text-xl font-medium text-zinc-900 mb-3">
                Ontvang uw resultaat
              </h3>
              <p className="text-zinc-600 leading-relaxed">
                Direct inzicht in uw risico met uitleg en aanbevelingen.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-16"
          >
            <Link to="/dba-check/vragenlijst" data-testid="bottom-cta-link">
              <Button 
                className="rounded-full px-10 py-6 text-base font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white btn-hover shadow-lg shadow-blue-500/20"
                data-testid="bottom-cta-btn"
              >
                Start nu de DBA Check
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-12 px-6 md:px-16 lg:px-24 bg-zinc-100">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-zinc-500" />
            <span className="text-sm font-medium text-zinc-600">Disclaimer</span>
          </div>
          <p className="text-sm text-zinc-500 leading-relaxed" data-testid="disclaimer-text">
            De DBA Check is een indicatieve zelftest en geeft geen juridisch of fiscaal advies. 
            De uitkomst is gebaseerd op uw eigen antwoorden en kan afwijken van de beoordeling 
            door de Belastingdienst. Voor definitief advies raden wij aan een specialist te raadplegen.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DBACheckLanding;
